// API service for Google Gemini integration
// IMPORTANT: All API calls go through the server proxy for credit tracking
// DO NOT use client-side SDK - it bypasses credit deduction
//
// "Nano Banana" is the official nickname for Gemini's image generation capability
// Model: gemini-2.5-flash-image (stable) or gemini-3-pro-image-preview (premium)
// Documentation: https://ai.google.dev/gemini-api/docs/image-generation

import { LIMITS, MODELS } from '../constants/app';
import logger from '../utils/logger';
import { supabase } from '../lib/supabase';
import type { ChatMessage, ConversationContext } from '../types/api';

interface GoogleAIResponse {
  content: string;
  type: 'text' | 'image';
  context: ConversationContext;
}

class GoogleAIService {
  private apiBase: string;

  constructor() {
    // Use relative URL in production, localhost for development
    this.apiBase = process.env.REACT_APP_API_BASE || (typeof window !== 'undefined' && window.location.hostname === 'localhost' ? 'http://localhost:3001' : '');
    console.log('✅ [GoogleAIService] Initialized - all requests go through server proxy at', this.apiBase);
  }

  async generateImage(
    prompt: string,
    context: ConversationContext | null = null,
    aspectRatio: string = '1:1',
    modelId?: string,  // Optional model override
    resolution?: string // For credit pricing tiers
  ): Promise<GoogleAIResponse> {
    try {
      // Use provided model or default to GOOGLE_IMAGE
      const model = modelId || MODELS.GOOGLE_IMAGE;
      const isGemini3Pro = model === 'gemini-3-pro-image-preview';
      
      // CRITICAL: Image generation MUST go through server for credit tracking
      // Client-side generation would bypass credit deduction entirely
      console.log('🎨 [GoogleAIService] generateImage called', {
        model,
        isGemini3Pro,
        prompt: prompt.substring(0, 100),
        aspectRatio,
        aspectRatioType: typeof aspectRatio,
        contextMessages: context?.messages?.length || 0,
        // Always use server for image generation (credit tracking)
        usingServer: true
      });

      // Always use server proxy for image generation - this ensures:
      // 1. Credits are properly reserved before generation
      // 2. Credits are confirmed on success, refunded on failure  
      // 3. All transactions are logged to the ledger
      // 4. API keys are not exposed to browser
      console.log('📤 [GoogleAIService] Sending to server with:', {
        prompt: prompt.substring(0, 50),
        aspectRatio,
        resolution,
        hasContext: !!context
      });
      
      // Get current user for credit tracking
      const { data: { user } } = await supabase.auth.getUser();
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (user?.id) {
        headers['x-user-id'] = user.id;
      }
      
      const res = await fetch(`${this.apiBase}/api/googleai/generate-image`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ prompt, model, aspectRatio, resolution: resolution || '1024px', context })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || err.message || `Server responded ${res.status}`);
      }
      const data = await res.json();
      const imageUrl: string = data.imageUrl;
      const updatedMessages: ChatMessage[] = [
        ...(context?.messages || []).slice(-LIMITS.MAX_CONTEXT_MESSAGES),
        { role: 'user' as const, content: `Image generation request: ${prompt}` },
        {
          role: 'assistant' as const,
          content: [
            { type: 'text' as const, text: `Generated image based on: ${prompt}` },
            { type: 'image' as const, imageUrl, mimeType: imageUrl.split(';')[0].split(':')[1] }
          ]
        }
      ].slice(-LIMITS.MAX_CONTEXT_MESSAGES);
      return { content: imageUrl, type: 'image', context: { messages: updatedMessages } };
    } catch (error) {
      const errorObj = error as { message?: string; status?: number; statusCode?: number; cause?: unknown };
      logger.error('Google AI API Error Details:', { message: errorObj.message, status: errorObj.status, statusCode: errorObj.statusCode, cause: errorObj.cause });
      let errorMessage = `Failed to generate image: ${errorObj.message || 'Unknown error'}`;
      if (errorObj.message?.includes('FAILED_PRECONDITION')) {
        errorMessage += '\n\n💡 Solution: Image generation requires billing to be enabled in Google AI Studio. Please visit https://aistudio.google.com/ and enable billing for your project.';
      } else if (errorObj.message?.includes('PERMISSION_DENIED')) {
        errorMessage += '\n\n💡 Solution: Your API key may not have access to image generation features. Check your API key permissions.';
      } else if (errorObj.message?.includes('RESOURCE_EXHAUSTED')) {
        errorMessage += '\n\n💡 Solution: You have exceeded the rate limit. Please wait a moment before trying again.';
      } else if (errorObj.message?.includes('NOT_FOUND')) {
        errorMessage += '\n\n💡 Solution: The image generation model may not be available in your region or with your current plan.';
      } else if (errorObj.message?.includes('Insufficient credits')) {
        errorMessage = errorObj.message; // Show credit error directly
      }
      throw new Error(errorMessage);
    }
  }

  async generateVideo(
    prompt: string,
    context: ConversationContext | null = null
  ): Promise<GoogleAIResponse> {
    const errorMessage = `Video generation with VEO-3 is not yet implemented.

This requires the @google/genai package which has a different API structure than the current @google/generative-ai package.

VEO-3 requires:
- The @google/genai package
- Using ai.models.generateVideos() method
- Polling for video completion
- Different authentication setup

Current limitation: This feature is planned for future implementation once the SDK is integrated.

Your prompt was: "${prompt}"`;

    logger.warn('VEO3 video generation attempted but not implemented');

    const updatedMessages: ChatMessage[] = [
      ...(context?.messages || []).slice(-LIMITS.MAX_CONTEXT_MESSAGES),
      { role: 'user' as const, content: `Video generation request: ${prompt}` },
      { role: 'assistant' as const, content: errorMessage }
    ].slice(-LIMITS.MAX_CONTEXT_MESSAGES);

    return { content: errorMessage, type: 'text', context: { messages: updatedMessages } };
  }

  async generateText(
    prompt: string,
    context: ConversationContext | null = null
  ): Promise<GoogleAIResponse> {
    try {
      // Console debug output for prompt being sent
      console.log('💬 [Google AI Text Request]', {
        model: MODELS.GOOGLE_TEXT,
        prompt,
        contextMessages: context?.messages?.length || 0
      });

      // All requests go through server proxy for credit tracking
      const { data: { user } } = await supabase.auth.getUser();
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (user?.id) {
        headers['x-user-id'] = user.id;
      }
      
      const res = await fetch(`${this.apiBase}/api/googleai/chat`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ prompt, model: MODELS.GOOGLE_TEXT })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Server responded ${res.status}`);
      }
      const data = await res.json();
      const responseContent = (data.content || '').toString();
      const updatedMessages: ChatMessage[] = [
        ...(context?.messages || []).slice(-LIMITS.MAX_CONTEXT_MESSAGES),
        { role: 'user' as const, content: prompt },
        { role: 'assistant' as const, content: responseContent }
      ].slice(-LIMITS.MAX_CONTEXT_MESSAGES);
      return { content: responseContent, type: 'text', context: { messages: updatedMessages } };

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      logger.error('Google AI API Error:', error);
      throw new Error(`Failed to generate text: ${errorMessage}`);
    }
  }

  isConfigured(): boolean {
    // Service is always configured in browser - we use server proxy (same-origin or explicit base)
    // In production, apiBase is '' (same-origin) which is valid, not unconfigured
    return typeof window !== 'undefined';
  }
}

const googleAIServiceInstance = new GoogleAIService();
export default googleAIServiceInstance;